import * as React from 'react';
import * as Yup from 'yup';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Formik, FormikActions, FormikProps } from 'formik';
import { mergeFields, processApiError } from 'common/utils/form';

import { FormMoneyInput } from 'common/components/form/FormMoneyInput';
import { Product } from 'common/api/models/Product';
import { ProductApi } from 'common/api/resources/ProductApi';
import { formatMoneyValue } from 'common/utils/format';
import { Analytics } from 'common/utils/Analytics';
import { FormInput } from 'common/components/form/FormInput';
import { Tag } from 'common/api/models/Tag';

interface ProductsDialogProps {
  product?: Product;
  tags: Tag[];
  onSave: (saved: string) => void;
  onCancel: () => void;
}

class ProductsFormValues {
  sku: string = '';
  name: string = '';
  type: string = 'NORMAL';
  tagId: string = '';
  accountingCode: string = '';
  description: string = '';
  price: string = '';
  isLearningProgram?: boolean;
  isLearningProgramText?: string = 'false';
}

export class ProductsDialog extends React.Component<ProductsDialogProps> {
  validationSchema = Yup.object().shape({
    sku: Yup.string()
      .required('Required')
      .matches(/^[a-zA-Z-0-9]+$/, 'SKU must be all letters and numbers'),
    name: Yup.string().required('Required'),
    type: Yup.string().required('Required'),
    price: Yup.number().min(1, 'Must be priced at least $1'),
    description: Yup.string().required('Required'),
    accountingCode: Yup.string().required('Required'),
    tagId: Yup.string().test('tagId', 'Tag or Learning Program must be set', function(this: any, data: String) {
      const { type, isLearningProgramText, sku, name } = this.options.parent;
      if (
        type === 'BADGE' &&
        data === undefined &&
        isLearningProgramText === 'false' &&
        !sku.toLowerCase().includes('retake') &&
        !name.toLowerCase().includes('retake')
      ) {
        return false;
      }
      return true;
    }),
    wcProductId: Yup.string().test('wcProductId', 'WC Product Id must be set', function(this: any, data: String) {
      const { type } = this.options.parent;
      if (type === 'LEARNING' && data === undefined) {
        return false;
      }
      return true;
    }),
  });

  initialValues() {
    if (this.props.product) {
      this.props.product.isLearningProgramText =
        this.props.product && this.props.product.isLearningProgram ? 'true' : 'false';
    }
    return {
      ...mergeFields(new ProductsFormValues(), this.props.product),
      price: formatMoneyValue(this.props.product ? this.props.product.price : 0),
    };
  }

  handleSubmit = (values: ProductsFormValues, actions: FormikActions<ProductsFormValues>) => {
    values.isLearningProgram =
      values &&
      (values.type === 'BADGE' || values.type === 'CERT_NEW' || values.type === 'LEARNING') &&
      values.isLearningProgramText === 'true'
        ? true
        : false;
    const productData = {
      ...values,
      type: Product.Type[values.type],
      price: Number(values.price),
    };
    if (productData.isLearningProgramText) {
      productData.isLearningProgramText = undefined;
    }
    const call =
      this.props.product && this.props.product.id
        ? ProductApi.updateProduct(this.props.product.id, productData)
        : ProductApi.createProduct(productData);
    call.then(
      res => {
        if (this.props.product && this.props.product.id) {
          Analytics.product.update();
        } else {
          Analytics.product.create();
        }
        this.props.onSave(this.props.product && this.props.product.id ? this.props.product.id : (res as any).id);
        actions.setSubmitting(false);
        actions.setStatus(undefined);
      },
      err => {
        processApiError<ProductsDialogProps, ProductsFormValues>(err, actions);
      }
    );
  };

  render() {
    const title = this.props.product && this.props.product.id ? 'Edit Product' : 'Add Product';
    return (
      <Modal size="md" isOpen={!!this.props.product} toggle={this.props.onCancel} keyboard={true}>
        <Formik
          initialValues={this.initialValues()}
          validationSchema={this.validationSchema}
          onSubmit={this.handleSubmit}
          render={(form: FormikProps<ProductsFormValues>) => (
            <form onSubmit={form.handleSubmit} noValidate={true}>
              <ModalHeader toggle={this.props.onCancel}>{title}</ModalHeader>
              <ModalBody>
                <div className="row">
                  <div className="col">
                    <FormInput id="sku" name="sku" label="SKU" autoComplete="off" />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormInput id="name" name="name" autoComplete="off" label="Name" />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormInput id="type" name="type" label="Type" tag="select" autoComplete="off">
                      <option value="NORMAL">Normal</option>
                      <option value="DUES">Dues</option>
                      <option value="BADGE">Badge</option>
                      <option value="LOCATIONS">Locations</option>
                      <option value="CERT_NEW">Cert New</option>
                      <option value="CERT_RENEW">Cert Renew</option>
                      <option value={Product.Type.WIKI}>Wiki</option>
                      <option value={Product.Type.GLOBALCONNECT}>Global Connect</option>
                      <option value={Product.Type.LEARNING}>LEARNING</option>
                    </FormInput>
                  </div>
                </div>
                {form.values.type &&
                  form.values.type === 'LEARNING' && (
                    <div className="row">
                      <div className="col">
                        <FormInput id="wcProductId" name="wcProductId" label="Woocommerce Product Id" />
                      </div>
                    </div>
                  )}
                {form.values.type &&
                  form.values.type === 'BADGE' &&
                  form.values.isLearningProgramText === 'false' && (
                    <div className="row">
                      <div className="col">
                        <FormInput id="tagId" name="tagId" label="Related Badge" tag="select" autoComplete="off">
                          <option value="">Select</option>
                          {this.props.tags &&
                            this.props.tags.map((tag, i) => (
                              <option value={tag.id} key={tag.id}>
                                {tag.shortName}
                              </option>
                            ))}
                        </FormInput>
                      </div>
                    </div>
                  )}
                <div className="row">
                  <div className="col">
                    <FormMoneyInput id="price" name="price" label="Price" currency="USD" />
                  </div>
                </div>
                {form.values.type &&
                  (form.values.type === 'BADGE' || form.values.type === 'CERT_NEW') &&
                  form.values.tagId === '' && (
                    <div className="row">
                      <div className="col">
                        <FormInput
                          id="isLearningProgramText"
                          name="isLearningProgramText"
                          label="Is Learning Program?"
                          tag="select"
                          autoComplete="off"
                        >
                          <option value="false">No</option>
                          <option value="true" selected={this.props.product && this.props.product.isLearningProgram}>
                            Yes
                          </option>
                        </FormInput>
                      </div>
                    </div>
                  )}

                <div className="row">
                  <div className="col">
                    <FormInput id="accountingCode" name="accountingCode" label="Accounting Code" />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormInput
                      tag="textarea"
                      id="description"
                      name="description"
                      label="Description"
                      autoComplete="off"
                    />
                  </div>
                </div>
                {form.status && <div className="alert alert-danger">{form.status}</div>}
              </ModalBody>
              <ModalFooter>
                <div>
                  <Button color="secondary" onClick={this.props.onCancel}>
                    Cancel
                  </Button>{' '}
                  <Button color="primary" type="submit">
                    Save
                  </Button>
                </div>
              </ModalFooter>
            </form>
          )}
        />
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Products/ProductsDialog.tsx